import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Globe } from "lucide-react";
import { useLang, useUI } from "../../lib/i18n";

export default function Header({ pagesVisibility = {} }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const lang = useLang();
  const ui = useUI();

  useEffect(() => setOpen(false), [location.pathname]);

  const isHidden = (key) => pagesVisibility[key] === false;

  const navItems = [
    { to: `/${lang}`, label: ui.home, key: "home", end: true },
    { to: `/${lang}/services`, label: ui.services, key: "services" },
    { to: `/${lang}/galerie`, label: ui.gallery, key: "gallery" },
    { to: `/${lang}/tarifs`, label: lang === "fr" ? "Tarifs" : "Pricing", key: "pricing" },
    { to: `/${lang}/blog`, label: ui.blog, key: "blog" },
    { to: `/${lang}/a-propos`, label: ui.about, key: "about" },
    { to: `/${lang}/contact`, label: ui.contactNav, key: "contact" },
  ].filter((n) => !isHidden(n.key));

  const switchLang = (newLang) => {
    if (newLang === lang) return;
    const newPath = location.pathname.replace(/^\/(fr|en)/, `/${newLang}`);
    navigate(newPath);
  };

  return (
    <header className="glass-nav fixed top-0 left-0 right-0 z-50" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-4">
        <Link to={`/${lang}`} className="font-serif text-2xl tracking-tight" data-testid="logo-link">
          Sarita<span className="text-[#C87A5E]">&apos;s</span> Photography
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `text-sm tracking-wide link-underline ${isActive ? "text-[#C87A5E]" : "text-[#2D2A26]"}`
              }
              data-testid={`nav-${n.key}`}
            >
              {n.label}
            </NavLink>
          ))}
          <div className="flex items-center gap-1 ml-2 border-l border-[#2D2A26]/15 pl-4">
            <Globe size={14} className="text-[#4A3F35] mr-1" />
            {["fr", "en"].map((l) => (
              <button
                key={l}
                onClick={() => switchLang(l)}
                className={`text-xs uppercase tracking-wider px-2 py-1 transition ${lang === l ? "text-[#C87A5E] font-semibold" : "text-[#4A3F35] hover:text-[#2D2A26]"}`}
                data-testid={`lang-switch-${l}`}
              >
                {l}
              </button>
            ))}
          </div>
          {!isHidden("contact") && (
            <Link to={`/${lang}/contact`} className="btn-primary !py-2.5 !px-5 text-xs" data-testid="header-cta">
              {ui.bookCTA}
            </Link>
          )}
        </nav>

        <button
          className="md:hidden text-[#2D2A26]"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#2D2A26]/10 bg-[#FDFBF7]" data-testid="mobile-menu">
          <nav className="flex flex-col px-6 py-6 gap-4">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-[#C87A5E]" : "text-[#2D2A26]"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="flex gap-2 pt-3 border-t border-[#2D2A26]/10 mt-2">
              {["fr", "en"].map((l) => (
                <button
                  key={l}
                  onClick={() => switchLang(l)}
                  className={`text-xs uppercase px-3 py-1.5 rounded-full ${lang === l ? "bg-[#C87A5E] text-white" : "bg-[#F6F3EC] text-[#4A3F35]"}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
